import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Network,
  CheckCircle2,
  AlertOctagon,
  Download,
  Sparkles,
  Activity,
  Volume2,
} from "lucide-react";
import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import AriaFeedback from "../components/AriaFeedback";
import ReasoningPanel, { reasoningForTopology } from "../components/ReasoningPanel";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";
import { renderMarkdown } from "../lib/markdown";
import { useAria } from "../context/AriaContext";

const STATUS_LABEL = {
  queued: "Queued",
  classifying: "Classifying case",
  running_agents: "Running clinical agents",
  synthesizing: "Synthesizing report",
  complete: "Complete",
  failed: "Failed",
};

export default function TopologyAnalysisDetail() {
  const { id } = useParams();
  const aria = useAria();
  const [statusDoc, setStatusDoc] = useState(null); // lightweight poll payload
  const [full, setFull] = useState(null); // full doc fetched after complete
  const [tab, setTab] = useState("report");
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const lastPhaseRef = useRef(null);
  const completionSpokenRef = useRef(false);

  const fetchStatus = async () => {
    try {
      const r = await api.get(`/topology-analysis/${id}/status`);
      setStatusDoc(r.data);
      return r.data;
    } catch (e) {
      setError(errMsg(e, "Failed to load status"));
      return null;
    }
  };

  const fetchFull = async () => {
    try {
      const r = await api.get(`/topology-analysis/${id}`);
      setFull(r.data);
    } catch (e) {
      setError(errMsg(e, "Failed to load full result"));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const s = await fetchStatus();
      if (!s) return;
      // ARIA — narrate phase transitions
      if (s.status && s.status !== lastPhaseRef.current) {
        lastPhaseRef.current = s.status;
        aria.narrateTopologyPhase(s.status);
      }
      if (s.status === "complete") {
        await fetchFull();
        toast.success("Analysis complete");
        return;
      }
      if (s.status === "failed") {
        toast.error(s.error || "Analysis failed");
        return;
      }
      pollRef.current = setTimeout(tick, 5000);
    };
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ARIA — read findings aloud once the full report is in
  useEffect(() => {
    if (full?.report && !completionSpokenRef.current) {
      completionSpokenRef.current = true;
      aria.announceTopologyComplete(full.report);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full?.report]);

  if (error && !statusDoc) {
    return (
      <Layout>
        <div className="text-sm text-red-300">{error}</div>
      </Layout>
    );
  }

  const isTerminal =
    statusDoc?.status === "complete" || statusDoc?.status === "failed";
  const pct = statusDoc?.progress_pct ?? 0;
  const agentsTotal = statusDoc?.agents_total ?? 0;
  const agentsDone = statusDoc?.agents_completed ?? 0;

  return (
    <Layout>
      <div className="kicker">Adaptive Agentic Topology</div>
      <h1
        data-testid="topology-detail-title"
        className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3"
      >
        <Network size={22} className="text-[var(--pa-gold)]" /> Topology
        Analysis
        {statusDoc?.status === "complete" && (
          <CheckCircle2 size={20} className="text-emerald-400" />
        )}
        {statusDoc?.status === "failed" && (
          <AlertOctagon size={20} className="text-red-400" />
        )}
      </h1>

      {statusDoc?.created_at && (
        <div className="mt-1 text-xs text-[var(--pa-text-dim)]">
          {new Date(statusDoc.created_at).toLocaleString()}
          {statusDoc.completed_at && (
            <>
              {" · runtime "}
              {Math.round(
                (new Date(statusDoc.completed_at) -
                  new Date(statusDoc.created_at)) /
                  1000
              )}
              s
            </>
          )}
        </div>
      )}

      {/* Progress card — visible while running */}
      {!isTerminal && (
        <ProgressCard
          status={statusDoc?.status}
          pct={pct}
          currentAgent={statusDoc?.current_agent}
          agentsDone={agentsDone}
          agentsTotal={agentsTotal}
          modules={statusDoc?.modules_activated}
          complexity={statusDoc?.complexity}
          caseSummary={statusDoc?.case_summary}
        />
      )}

      {/* Failure card */}
      {statusDoc?.status === "failed" && (
        <div
          data-testid="topology-failed"
          className="mt-6 border border-red-500/40 bg-red-500/10 rounded-md p-5"
        >
          <div className="flex items-center gap-2 text-red-300 font-semibold">
            <AlertOctagon size={16} /> Analysis failed
          </div>
          {statusDoc.error && (
            <p className="mt-2 text-sm text-[var(--pa-text-muted)]">
              {statusDoc.error}
            </p>
          )}
          <Link
            to="/topology-analysis/new"
            className="inline-block mt-4 text-xs text-[var(--pa-gold)] hover:underline"
          >
            Try a new analysis →
          </Link>
        </div>
      )}

      {/* Complete view — header chips + tabs */}
      {statusDoc?.status === "complete" && (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="topology-chips">
            <Chip label={`Complexity: ${statusDoc.complexity || "—"}`} accent />
            <Chip label={`${(statusDoc.modules_activated || []).length} agents`} />
            {(statusDoc.modules_activated || []).map((m) => (
              <Chip key={m} label={m.replace(/-/g, " ")} subtle />
            ))}
          </div>

          {statusDoc.case_summary && (
            <p
              data-testid="topology-summary"
              className="mt-5 text-sm text-[var(--pa-text)] leading-relaxed bg-[var(--pa-bg-2)] border-l-2 border-[var(--pa-gold)] p-4 rounded-r"
            >
              {statusDoc.case_summary}
            </p>
          )}

          {full?.report && (
            <div className="mt-4">
              <button
                onClick={() => aria.summarizeOnDemand(full.report)}
                disabled={aria.speaking}
                data-testid="topology-aria-summarize"
                className="inline-flex items-center gap-2 text-xs text-[var(--pa-gold)] hover:underline disabled:opacity-50"
              >
                <Volume2 size={12} />
                {aria.speaking ? "ARIA is speaking…" : "ARIA — read summary aloud"}
              </button>
            </div>
          )}

          {full && <AiOutputDisclaimer className="mt-4" />}

          {full?.report && (
            <ReasoningPanel
              surface="topology"
              reasoning={reasoningForTopology(full)}
            />
          )}

          {full?.report && (
            <AriaFeedback
              surface="topology"
              ariaResponse={full.case_summary || full.report.slice(0, 1000)}
              originalQuery={full.case_input || null}
              resourceId={full.id}
            />
          )}

          <div
            data-testid="topology-tabs"
            className="mt-6 flex gap-2 border-b border-[var(--pa-border)] overflow-x-auto"
          >
            {[
              ["report", "Unified Report"],
              ["agents", "Agent Outputs"],
              ["topology", "Topology Config"],
              ["raw", "Raw Case Input"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`topology-tab-${k}`}
                className={`px-4 py-2 text-sm border-b-2 -mb-px whitespace-nowrap transition ${
                  tab === k
                    ? "border-[var(--pa-gold)] text-[var(--pa-gold)]"
                    : "border-transparent text-[var(--pa-text-muted)] hover:text-[var(--pa-text)]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {!full && (
            <div className="mt-6 flex items-center gap-2 text-[var(--pa-text-muted)] text-sm">
              <Loader2 size={14} className="animate-spin" /> Loading full
              record…
            </div>
          )}

          {full && tab === "report" && (
            <ReportTab report={full.report} id={full.id} />
          )}
          {full && tab === "agents" && (
            <AgentsTab outputs={full.agent_outputs} />
          )}
          {full && tab === "topology" && <TopologyTab config={full.topology_config} />}
          {full && tab === "raw" && (
            <pre
              data-testid="topology-raw"
              className="mt-6 text-sm whitespace-pre-wrap bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-4 text-[var(--pa-text)] font-sans leading-relaxed"
            >
              {full.case_input}
            </pre>
          )}
        </>
      )}
    </Layout>
  );
}

function Chip({ label, accent, subtle }) {
  const cls = accent
    ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/15 text-[var(--pa-gold)]"
    : subtle
    ? "border-[var(--pa-border)] bg-[var(--pa-bg-2)] text-[var(--pa-text-muted)]"
    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
  return (
    <span
      className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}
    >
      {label}
    </span>
  );
}

function ProgressCard({
  status,
  pct,
  currentAgent,
  agentsDone,
  agentsTotal,
  modules,
  complexity,
  caseSummary,
}) {
  return (
    <section
      data-testid="topology-progress"
      className="mt-6 border border-[var(--pa-gold)]/30 bg-[var(--pa-gold)]/5 rounded-md p-5"
    >
      <div className="flex items-center gap-2">
        <Loader2 size={16} className="animate-spin text-[var(--pa-gold)]" />
        <span className="font-serif text-lg text-[var(--pa-text)]">
          {STATUS_LABEL[status] || status || "Queued"}…
        </span>
        <span className="ml-auto text-2xl font-serif text-[var(--pa-gold)]">
          {pct}%
        </span>
      </div>

      <div className="mt-3 w-full h-2 bg-[var(--pa-bg-2)] rounded overflow-hidden">
        <div
          data-testid="topology-progress-bar"
          className="h-2 bg-[var(--pa-gold)] transition-all duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      {complexity && (
        <div className="mt-4 text-xs text-[var(--pa-text-muted)]">
          Complexity: <span className="text-[var(--pa-gold)] font-semibold capitalize">{complexity}</span>
          {agentsTotal > 0 && (
            <> · agents <span className="text-[var(--pa-text)] font-semibold">{agentsDone}/{agentsTotal}</span></>
          )}
          {currentAgent && (
            <> · currently <span className="text-[var(--pa-gold)]">{currentAgent.replace(/-/g, " ")}</span></>
          )}
        </div>
      )}

      {caseSummary && (
        <p className="mt-3 text-sm text-[var(--pa-text)] leading-relaxed">
          {caseSummary}
        </p>
      )}

      {modules && modules.length > 0 && (
        <div className="mt-4">
          <div className="kicker mb-2">Pipeline</div>
          <ol className="space-y-1.5">
            {modules.map((m, i) => {
              const isCurrent = currentAgent === m;
              const isDone = i < agentsDone;
              return (
                <li
                  key={m}
                  className={`text-xs flex items-center gap-2 ${
                    isDone
                      ? "text-emerald-400"
                      : isCurrent
                      ? "text-[var(--pa-gold)]"
                      : "text-[var(--pa-text-dim)]"
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center border ${
                    isDone ? 'border-emerald-400' : isCurrent ? 'border-[var(--pa-gold)]' : 'border-[var(--pa-border)]'
                  }`}>
                    {isDone ? "✓" : i + 1}
                  </span>
                  <span className="capitalize">{m.replace(/-/g, " ")}</span>
                  {isCurrent && (
                    <Loader2 size={11} className="animate-spin ml-1" />
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      <div className="mt-5 text-[11px] text-[var(--pa-text-dim)] leading-relaxed">
        This page polls every 5 seconds. You can safely close the tab — the
        analysis continues server-side and the result will be waiting in your
        list when you return.
      </div>
    </section>
  );
}

function ReportTab({ report, id }) {
  const dl = () => {
    const blob = new Blob([report || ""], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `topology-${id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };
  if (!report) return <div className="mt-6 text-sm text-[var(--pa-text-dim)]">No report.</div>;
  return (
    <div className="mt-6" data-testid="topology-report">
      <div className="flex items-center justify-between mb-3">
        <div className="kicker">Unified clinical case report</div>
        <button
          onClick={dl}
          className="flex items-center gap-1 text-xs text-[var(--pa-gold)] hover:underline"
          data-testid="topology-download"
        >
          <Download size={12} /> Download .md
        </button>
      </div>
      <div
        className="prose-pa text-sm leading-relaxed text-[var(--pa-text)] bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-5"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(report) }}
      />
    </div>
  );
}

function AgentsTab({ outputs }) {
  const entries = Object.entries(outputs || {});
  if (!entries.length)
    return <div className="mt-6 text-sm text-[var(--pa-text-dim)]">No agent outputs.</div>;
  return (
    <div className="mt-6 space-y-4" data-testid="topology-agents">
      {entries.map(([slug, output]) => (
        <details
          key={slug}
          className="border border-[var(--pa-border)] bg-[var(--pa-bg-2)] rounded"
          data-testid={`agent-${slug}`}
        >
          <summary className="cursor-pointer px-4 py-3 font-serif text-base text-[var(--pa-text)] flex items-center gap-2 hover:text-[var(--pa-gold)]">
            <Activity size={14} className="text-[var(--pa-gold)]" />
            <span className="capitalize">{slug.replace(/-/g, " ")}</span>
            <span className="text-[10px] text-[var(--pa-text-dim)] ml-auto">
              {output ? `${output.length.toLocaleString()} chars` : "empty"}
            </span>
          </summary>
          <div
            className="px-4 pb-4 text-sm text-[var(--pa-text-muted)] leading-relaxed prose-pa"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(output) }}
          />
        </details>
      ))}
    </div>
  );
}

function TopologyTab({ config }) {
  if (!config) return <div className="mt-6 text-sm text-[var(--pa-text-dim)]">No config.</div>;
  return (
    <div className="mt-6 space-y-4" data-testid="topology-config">
      <div className="grid sm:grid-cols-3 gap-3">
        <Box label="Complexity">
          <span className="capitalize font-semibold text-[var(--pa-gold)]">
            {config.complexity}
          </span>
          {config.complexity_score != null && (
            <span className="ml-2 text-[var(--pa-text-dim)]">
              ({config.complexity_score}/100)
            </span>
          )}
        </Box>
        <Box label="Activated agents">
          {(config.activated_modules || []).length}
        </Box>
        <Box label="Parallel agents">
          {(config.parallel_modules || []).length}
        </Box>
      </div>

      {config.reasoning && (
        <div>
          <div className="kicker mb-1">Classifier reasoning</div>
          <p className="text-sm text-[var(--pa-text-muted)] leading-relaxed bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-4">
            {config.reasoning}
          </p>
        </div>
      )}

      {config.key_flags?.length > 0 && (
        <div>
          <div className="kicker mb-2">Key clinical flags</div>
          <div className="flex flex-wrap gap-1.5">
            {config.key_flags.map((f, i) => (
              <span
                key={i}
                className="text-[11px] px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="kicker mb-2">Module priority order</div>
        <ol className="space-y-1 text-xs">
          {(config.activated_modules || []).map((m, i) => {
            const isParallel = (config.parallel_modules || []).includes(m);
            return (
              <li
                key={m}
                className="flex items-center gap-2 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded px-3 py-2"
              >
                <span className="w-5 h-5 rounded-full bg-[var(--pa-gold)] text-[var(--pa-bg)] text-[10px] font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-[var(--pa-text)] capitalize">
                  {m.replace(/-/g, " ")}
                </span>
                {isParallel && (
                  <span className="ml-auto text-[10px] text-emerald-300">
                    parallel-safe
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function Box({ label, children }) {
  return (
    <div className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-4">
      <div className="text-[10px] uppercase tracking-wider text-[var(--pa-text-dim)] mb-2">
        {label}
      </div>
      <div className="font-serif text-xl text-[var(--pa-text)]">{children}</div>
    </div>
  );
}
